import type { Href } from 'expo-router';
import { Platform } from 'react-native';
import * as SecureStore from 'expo-secure-store';
import {
  CATALOG_JOB_ROUTE_ID_PATTERN,
  isPublicHandleLike,
  normalizePublicHandle,
} from '@tabbio/utils';
import {
  readWebStorage,
  removeWebStorage,
  writeWebStorage,
} from './web-storage';
import {
  decodeOAuthPostAuthTargetQuery,
  isEncodedOAuthTarget,
} from './oauth-consent';
import { SEEKER_ONLY_LAUNCH } from './launch-scope';
import {
  buildCliConnectPath,
  decodeCliConnectPostAuthTarget,
} from './cli-connect';
import { clearStoredResumeHandoff } from './resume-handoff-storage';

export type PostAuthTarget =
  | 'root'
  | 'cv-email-share'
  | 'cv-email-edit'
  | 'cv-email-preferences'
  | 'skills-cv'
  | 'resume-import'
  | 'onboarding-profile-setup'
  | 'onboarding-intent'
  | 'password-bootstrap'
  | `cli-connect:${string}`
  | `automation-run:${string}`
  | `cv-access:${string}`
  | `cv-handoff:${string}`
  | `cv-grant:${string}`
  | `jobs:${string}`
  | `join:${string}`
  | `messages:${string}`
  | `oauth:${string}`
  | `report:${string}`
  | `skill:${string}`
  | `skill-run:${string}`;

// Dash-form catalog id (`native-<id>` / `external-<id>`) as used in job route
// URLs: the colon form would break as a path segment. `[id].tsx` decodes it
// via fromCatalogJobRouteId; the pattern is owned by @tabbio/utils.
const JOBS_TARGET_ID_PATTERN = CATALOG_JOB_ROUTE_ID_PATTERN;

// A Nova (Tasks) run id: Prisma cuid, as carried by the result email's
// /auto-apply?runId=<id> link.
const AUTOMATION_RUN_ID_PATTERN = /^c[a-z0-9]{24}$/;

const PREFIXED_POST_AUTH_TARGET_KINDS = [
  'cli-connect',
  'automation-run',
  'cv-access',
  'cv-handoff',
  'cv-grant',
  'jobs',
  'join',
  'messages',
  'oauth',
  'report',
  'skill',
  'skill-run',
] as const;

type PrefixedPostAuthTargetKind =
  (typeof PREFIXED_POST_AUTH_TARGET_KINDS)[number];

export type PostAuthTargetKind =
  | 'root'
  | 'cv-email-share'
  | 'cv-email-edit'
  | 'cv-email-preferences'
  | 'skills-cv'
  | 'resume-import'
  | 'onboarding-profile-setup'
  | 'onboarding-intent'
  | 'password-bootstrap'
  | PrefixedPostAuthTargetKind;

/**
 * Collapse a target to its kind. Written as an exhaustive switch on purpose:
 * adding a variant to PostAuthTarget without listing it here (or in
 * PREFIXED_POST_AUTH_TARGET_KINDS) fails type-check, which is how a target
 * silently falls out of the classifications below (issue #246).
 */
export function getPostAuthTargetKind(
  target: PostAuthTarget,
): PostAuthTargetKind {
  switch (target) {
    case 'root':
    case 'cv-email-share':
    case 'cv-email-edit':
    case 'cv-email-preferences':
    case 'skills-cv':
    case 'resume-import':
    case 'onboarding-profile-setup':
    case 'onboarding-intent':
    case 'password-bootstrap':
      return target;
    default: {
      // Compile-time guard: whatever remains of the union must be one of the
      // `<kind>:<value>` forms above.
      const prefixed: `${PrefixedPostAuthTargetKind}:${string}` = target;
      return (
        PREFIXED_POST_AUTH_TARGET_KINDS.find((kind) =>
          prefixed.startsWith(`${kind}:`),
        ) ?? 'root'
      );
    }
  }
}

/**
 * Targets that must survive the onboarding flow: completeOnboarding resumes
 * them (the resume-import screen / the job the user applied to / the shared CV
 * the invite pointed at). Everything else is just an onboarding entry signal
 * and is safe to clear mid-flow.
 *
 * Every kind is listed explicitly: `satisfies Record<PostAuthTargetKind, …>`
 * makes an unclassified kind a type error rather than a silently dropped
 * target.
 */
const POST_AUTH_TARGET_SURVIVES_ONBOARDING = {
  root: false,
  'cv-email-share': true,
  'cv-email-edit': true,
  'cv-email-preferences': true,
  'skills-cv': true,
  'resume-import': true,
  'onboarding-profile-setup': false,
  'onboarding-intent': false,
  'password-bootstrap': false,
  // `tabbio login` in a terminal opened /cli/connect for someone who had to
  // sign UP first. Same reasoning as `oauth` below: the CLI is still waiting
  // on its loopback listener, so the hand-off has to outlive onboarding or the
  // terminal never hears back. app/index.tsx replays it; the connect page
  // clears it once it renders for a signed-in user.
  'cli-connect': true,
  // A Tasks result link (email / notification) opened while signed out: the
  // visitor must land on that run after signing in, even through onboarding.
  'automation-run': true,
  // Invite-token redemption shim; unchanged, it is not resumed at onboarding
  // completion today.
  'cv-access': false,
  // A CV someone was SENT: a recipient without an account signs UP from the
  // invite, so the token must outlive onboarding. The ai-input step auto-claims
  // it (with intent 'update') and clears the target itself; if onboarding
  // resumes past ai-input the target instead rides to completeOnboarding, which
  // resumes the /cv-handoff/<token> chooser shim.
  'cv-handoff': true,
  // An already-accepted shared-CV grant: a recipient without an account signs
  // UP from the invite, so the target has to outlive onboarding or they land on
  // their own CV instead of the shared one (issue #246).
  'cv-grant': true,
  jobs: true,
  // A visitor who clicked "Join the talent network" on a public company page
  // and had to sign UP: the join intent must outlive onboarding exactly like a
  // public job apply, or the new user lands on generic app home instead of the
  // connect-join dialog they came for.
  join: true,
  messages: true,
  // A "Continue with Tabbio" button on a partner site that sent a brand new
  // user here: they sign UP, so the authorize request has to outlive
  // onboarding or the partner's callback never fires and the person is left on
  // app home wondering what happened to the site they came from. app/index.tsx
  // replays it once the runtime opens; the consent screen clears it.
  oauth: true,
  // A visitor who signs up from a public CV purely to report it has to land
  // back on the report form once onboarding finishes, or the abuse report is
  // silently dropped and they have no way back to it.
  report: true,
  // A public skill share link ("Open in Tabbio"). Must survive the fresh-signup
  // rewrite so the skills-handoff shim runs at all; the shim then applies the
  // skill as an onboarding preference and clears the target itself, so it is
  // NOT resumed again after onboarding completes.
  skill: true,
  'skill-run': true,
} satisfies Record<PostAuthTargetKind, boolean>;

export function postAuthTargetSurvivesOnboarding(target: PostAuthTarget) {
  return POST_AUTH_TARGET_SURVIVES_ONBOARDING[getPostAuthTargetKind(target)];
}
const POST_AUTH_TARGET_STORAGE_KEY = 'tabbio_post_auth_target';
let nativePostAuthTargetCache: string | null = null;

function readStorage(key: string) {
  if (Platform.OS === 'web') {
    return readWebStorage(key, 'session');
  }

  return nativePostAuthTargetCache;
}

function writeStorage(key: string, value: string) {
  if (Platform.OS === 'web') {
    writeWebStorage(key, value, 'session');
    return;
  }

  nativePostAuthTargetCache = value;
  void SecureStore.setItemAsync(key, value);
}

function removeStorage(key: string) {
  if (Platform.OS === 'web') {
    removeWebStorage(key, 'session');
    return;
  }

  void SecureStore.deleteItemAsync(key);
}

export function normalizePostAuthTarget(value?: string | string[]) {
  const target = Array.isArray(value) ? value[0] : value;

  if (
    target === 'cv-email-share' ||
    target === 'cv-email-edit' ||
    target === 'cv-email-preferences'
  ) {
    return target;
  }

  if (target === 'skills-cv') {
    return 'skills-cv';
  }

  if (target === 'resume-import') {
    return 'resume-import';
  }

  if (target === 'onboarding-profile-setup') {
    return 'onboarding-profile-setup';
  }

  if (target === 'onboarding-intent') {
    // Limited launch: the intent picker is hidden, so old links resume at the
    // shared onboarding gate and the provider advances seeker users to AI input.
    if (SEEKER_ONLY_LAUNCH) {
      return 'onboarding-profile-setup';
    }

    return 'onboarding-intent';
  }

  if (target === 'password-bootstrap') {
    return 'password-bootstrap';
  }

  if (target?.startsWith('automation-run:')) {
    const runId = target.slice('automation-run:'.length).trim();
    return AUTOMATION_RUN_ID_PATTERN.test(runId)
      ? (`automation-run:${runId}` as const)
      : 'root';
  }

  if (target?.startsWith('cv-access:')) {
    const token = target.slice('cv-access:'.length).trim();
    return token ? (`cv-access:${token}` as const) : 'root';
  }

  if (target?.startsWith('cv-handoff:')) {
    const token = target.slice('cv-handoff:'.length).trim();
    return token ? (`cv-handoff:${token}` as const) : 'root';
  }

  // An already-accepted whole-profile CV grant (issue #246). Distinct from
  // `cv-access:`: that carries an invite TOKEN to redeem, this carries the
  // grant ID of access the user already holds.
  if (target?.startsWith('cv-grant:')) {
    const grantId = target.slice('cv-grant:'.length).trim();
    return grantId ? (`cv-grant:${grantId}` as const) : 'root';
  }

  if (target?.startsWith('jobs:')) {
    const raw = target.slice('jobs:'.length).trim();
    // Connect-style apply intent rides as a `:apply` suffix (`jobs:<id>:apply`,
    // from /jobs-handoff?id=<id>&intent=apply). The id itself stays colon-free
    // (JOBS_TARGET_ID_PATTERN), so the suffix is unambiguous.
    const applyIntent = raw.endsWith(':apply');
    const id = applyIntent ? raw.slice(0, -':apply'.length) : raw;
    if (!JOBS_TARGET_ID_PATTERN.test(id)) {
      return 'root';
    }
    return applyIntent
      ? (`jobs:${id}:apply` as const)
      : (`jobs:${id}` as const);
  }

  // "Join {company}'s talent network" from a public company page
  // (/jobs-handoff?join=<companySlug>). Company slugs share the public-handle
  // grammar (the API normalizes them the same way on submit).
  if (target?.startsWith('join:')) {
    const slug = normalizePublicHandle(target.slice('join:'.length));
    return isPublicHandleLike(slug) ? (`join:${slug}` as const) : 'root';
  }

  if (target?.startsWith('messages:')) {
    const handle = normalizePublicHandle(target.slice('messages:'.length));
    return isPublicHandleLike(handle)
      ? (`messages:${handle}` as const)
      : 'root';
  }

  // The whole authorize query, base64url encoded (lib/oauth-consent.ts). Only
  // the charset is checked here: the consent screen re-parses the query and the
  // API re-validates every parameter, so a well-formed but bogus request lands
  // on the error page rather than being silently rewritten to root.
  if (target?.startsWith('oauth:')) {
    const encoded = target.slice('oauth:'.length).trim();
    return isEncodedOAuthTarget(encoded)
      ? (`oauth:${encoded}` as const)
      : 'root';
  }

  // The Tabbio CLI loopback hand-off (/cli/connect), parked whole as one
  // base64url value like `oauth:`. Unlike `oauth:` it is fully re-validated
  // here (port range, state shape), because the page it resumes redirects the
  // browser: a target that does not decode to a valid 127.0.0.1 hand-off is
  // dropped to root rather than carried forward.
  if (target?.startsWith('cli-connect:')) {
    const trimmed = `cli-connect:${target.slice('cli-connect:'.length).trim()}` as const;
    return decodeCliConnectPostAuthTarget(trimmed) ? trimmed : 'root';
  }

  if (target?.startsWith('report:')) {
    const handle = normalizePublicHandle(target.slice('report:'.length));
    return isPublicHandleLike(handle) ? (`report:${handle}` as const) : 'root';
  }

  if (target?.startsWith('skill:') || target?.startsWith('skill-run:')) {
    const kind = target.startsWith('skill-run:') ? 'skill-run' : 'skill';
    const skillId = target.slice(kind.length + 1).trim().toLowerCase();
    // Same slug shape the onboarding skill-style resolver accepts.
    return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(skillId) && skillId.length <= 64
      ? (`${kind}:${skillId}` as const)
      : 'root';
  }

  return 'root';
}

/**
 * The post-auth target for a Tasks run deep link (`/auto-apply?runId=<cuid>`),
 * or null for any other location. Used on web to keep the run across the
 * signed-out redirect to login.
 */
export function automationRunTargetFromLocation(location: {
  pathname: string;
  search: string;
}): `automation-run:${string}` | null {
  const pathname = location.pathname.replace(/\/+$/, '');
  if (pathname !== '/auto-apply') {
    return null;
  }
  const runId = new URLSearchParams(location.search).get('runId')?.trim();
  if (!runId) {
    return null;
  }
  const target = normalizePostAuthTarget(`automation-run:${runId}`);
  return target.startsWith('automation-run:')
    ? (target as `automation-run:${string}`)
    : null;
}

/**
 * The run id a stored `automation-run:<id>` target points at, else null.
 */
export function automationRunIdOfTarget(target: PostAuthTarget): string | null {
  return getPostAuthTargetKind(target) === 'automation-run'
    ? target.slice('automation-run:'.length)
    : null;
}

export function getPostAuthRedirectHref(target: PostAuthTarget = 'root') {
  return {
    pathname: '/auth-complete',
    params: { next: target },
  } satisfies Href;
}

/**
 * Persist the target and navigate to /auth-complete in one step. The
 * auth-state flip that follows sign-in remounts the root navigator
 * mid-transition (the URL resets to '/'), so the ?next= param riding on
 * /auth-complete can be lost: app/index.tsx falls back to the stored copy.
 * Always writing also flushes any stale target from an earlier sign-in.
 *
 * The resume-handoff token gets the same flush: it is only valid for a
 * sign-in that came through the website funnel (target `resume-import`). A
 * token still stored when any other sign-in commits is a leftover from an
 * earlier session or account on this device, and left in place it would be
 * claimed on the first onboarding screen (incident 2026-10-04).
 *
 * Every sign-in surface MUST route through this instead of calling
 * router.replace(getPostAuthRedirectHref(...)) directly, or the surface
 * silently reintroduces the lost-job race.
 */
export function commitPostAuthRedirect(
  router: { replace: (href: Href) => void },
  target: PostAuthTarget = 'root',
) {
  savePostAuthTarget(target);
  if (target !== 'resume-import') {
    void clearStoredResumeHandoff();
  }
  router.replace(getPostAuthRedirectHref(target));
}

export function getPostAuthRedirectPath(target: PostAuthTarget = 'root') {
  return `/auth-complete?next=${target}`;
}

export function savePostAuthTarget(target: PostAuthTarget) {
  writeStorage(POST_AUTH_TARGET_STORAGE_KEY, target);
}

export async function readPostAuthTargetAsync() {
  if (Platform.OS === 'web') {
    return readPostAuthTarget();
  }

  if (nativePostAuthTargetCache) {
    return normalizePostAuthTarget(nativePostAuthTargetCache);
  }

  try {
    nativePostAuthTargetCache = await SecureStore.getItemAsync(
      POST_AUTH_TARGET_STORAGE_KEY,
    );
  } catch {
    nativePostAuthTargetCache = null;
  }
  return normalizePostAuthTarget(nativePostAuthTargetCache ?? undefined);
}

export function readPostAuthTarget() {
  return normalizePostAuthTarget(
    readStorage(POST_AUTH_TARGET_STORAGE_KEY) ?? undefined,
  );
}

export function clearPostAuthTarget() {
  nativePostAuthTargetCache = null;
  removeStorage(POST_AUTH_TARGET_STORAGE_KEY);
}

export function getPostAuthDestinationHref(target: PostAuthTarget = 'root') {
  if (
    target === 'cv-email-share' ||
    target === 'cv-email-edit' ||
    target === 'cv-email-preferences'
  ) {
    return `/cv-email?action=${target.slice('cv-email-'.length)}` satisfies Href;
  }

  if (target === 'skills-cv') {
    // Keep this unguarded until auth/bootstrap/onboarding opens the app group.
    return '/skills-handoff?tab=cv' satisfies Href;
  }

  if (target === 'onboarding-profile-setup') {
    // profile-setup was removed; ai-input is the first onboarding step. Native
    // still routes through the (onboarding) index (which renders the wizard);
    // the `entry` param is the generic "start onboarding" signal the provider
    // reads to resolve the first step.
    return (
      Platform.OS === 'web'
        ? '/(onboarding)/ai-input'
        : {
            pathname: '/(onboarding)',
            params: { entry: 'profile-setup' },
          }
    ) satisfies Href;
  }

  if (target === 'onboarding-intent') {
    return (
      Platform.OS === 'web'
        ? {
            pathname: '/(onboarding)/intent',
            params: { entry: 'intent' },
          }
        : {
            pathname: '/(onboarding)',
            params: { entry: 'intent' },
          }
    ) satisfies Href;
  }

  if (target === 'password-bootstrap') {
    return '/password-bootstrap' satisfies Href;
  }

  if (target === 'resume-import') {
    return '/resume-import' satisfies Href;
  }

  if (target.startsWith('automation-run:')) {
    const runId = target.slice('automation-run:'.length);
    // The Tasks board opens the run panel for ?runId= (and consumes the param).
    return `/auto-apply?runId=${encodeURIComponent(runId)}` satisfies Href;
  }

  if (target.startsWith('cv-access:')) {
    const token = target.slice('cv-access:'.length);
    return `/cv-access/${encodeURIComponent(token)}` satisfies Href;
  }

  if (target.startsWith('cv-handoff:')) {
    const token = target.slice('cv-handoff:'.length);
    return `/cv-handoff/${encodeURIComponent(token)}` satisfies Href;
  }

  if (target.startsWith('cv-grant:')) {
    const grantId = target.slice('cv-grant:'.length);
    // The UNGUARDED shim, never /cv/access/<id> straight into (app): same
    // reasoning as jobs below.
    return `/cv-grant/${encodeURIComponent(grantId)}` satisfies Href;
  }

  if (target.startsWith('jobs:')) {
    const raw = target.slice('jobs:'.length);
    const applyIntent = raw.endsWith(':apply');
    const id = applyIntent ? raw.slice(0, -':apply'.length) : raw;
    // Route to the UNGUARDED jobs-handoff shim, never straight to the guarded
    // /(app)/jobs/<id>. auth-complete fires a one-shot router.replace to this
    // destination; if it pointed at the (app) group and the protected runtime
    // isn't open yet (e.g. a new applicant who hasn't finished onboarding), the
    // replace no-ops and the user is stranded on "Finishing sign in". The shim
    // stays mounted, re-renders as auth state settles, and does the auth-aware
    // navigation into (app): same pattern as cv-handoff.
    return `/jobs-handoff?id=${encodeURIComponent(id)}${
      applyIntent ? '&intent=apply' : ''
    }` satisfies Href;
  }

  if (target.startsWith('join:')) {
    const slug = target.slice('join:'.length);
    // The UNGUARDED shim again, same reasoning as jobs above.
    return `/jobs-handoff?join=${encodeURIComponent(slug)}` satisfies Href;
  }

  if (target.startsWith('messages:')) {
    const handle = target.slice('messages:'.length);
    return `/messages-handoff?handle=${encodeURIComponent(handle)}` satisfies Href;
  }

  if (target.startsWith('oauth:')) {
    const query = decodeOAuthPostAuthTargetQuery(target);
    // The consent route is registered unguarded on both platforms, so it can be
    // the destination directly: it does its own auth-aware work and never needs
    // the (app) group to be mounted.
    return (query ? `/oauth/authorize?${query}` : '/') satisfies Href;
  }

  if (target.startsWith('cli-connect:')) {
    const params = decodeCliConnectPostAuthTarget(target);
    // /cli/connect is unguarded (it does its own auth-aware work), so it can be
    // the destination directly, exactly like the OAuth consent route above.
    return (params ? buildCliConnectPath(params) : '/') satisfies Href;
  }

  if (target.startsWith('report:')) {
    const handle = target.slice('report:'.length);
    // The UNGUARDED shim again, same reasoning as jobs/messages above.
    return `/report-handoff?handle=${encodeURIComponent(handle)}` satisfies Href;
  }

  if (target.startsWith('skill:')) {
    const skillId = target.slice('skill:'.length);
    // The UNGUARDED shim again, same reasoning as jobs/messages above.
    return `/skills-handoff?id=${encodeURIComponent(skillId)}` satisfies Href;
  }

  if (target.startsWith('skill-run:')) {
    const skillId = target.slice('skill-run:'.length);
    return `/skills-handoff?id=${encodeURIComponent(skillId)}&mode=run` satisfies Href;
  }

  return '/' satisfies Href;
}
